import { FLOW_TRIGGERS, ORDER_TRIGGERS, type FlowRecipeId, type FlowTrigger } from '@cartly/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  KpiCard,
  Modal,
  PageHeader,
  SelectField,
  Spinner,
  TextField,
  useConfirm,
  useToast,
  type Tone,
} from '@cartly/ui';
import { Pause, Play, Plus, Trash2, Zap } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../api/client';
import type { Attribution } from '../../api/types';
import { formatMoney, formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { ParamsEditor, normalizeParams, type ParamSource } from '../whatsapp/Campaigns';
import { TRIGGER_LABEL } from './labels';
import { templatesForPhone, useWhatsappChoices } from './useWhatsappChoices';

interface FlowRow {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'paused' | 'archived';
  trigger: FlowTrigger | null;
  activeVersion: number | null;
  hasUnpublishedDraft: boolean;
  updatedAt: string;
}
interface RecipeRow {
  id: FlowRecipeId;
  title: string;
  trigger: FlowTrigger;
  summary: string;
}
interface Usage {
  day: string;
  runs: { used: number; blocked: number; limit: number };
  messages: { used: number; blocked: number; limit: number };
}

const STATUS_TONE: Record<FlowRow['status'], Tone> = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
};
const STATUS_LABEL: Record<FlowRow['status'], string> = {
  draft: 'Not published',
  active: 'Live',
  paused: 'Paused',
  archived: 'Archived',
};

const BLANK = 'blank';

function NewAutomation({
  open,
  recipes,
  tenantId,
  onClose,
}: {
  open: boolean;
  recipes: RecipeRow[];
  tenantId: string | undefined;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const submit = useSubmit();
  const { can } = useSession();
  const choices = useWhatsappChoices(tenantId, can('channels:read'));
  const phones = choices.data?.phones ?? [];
  const templates = choices.data?.templates ?? [];

  const [name, setName] = useState('');
  const [source, setSource] = useState<string>(recipes[0]?.id ?? BLANK);
  const [trigger, setTrigger] = useState<FlowTrigger>('order.created');
  const [phoneId, setPhoneId] = useState('');
  const [tpl, setTpl] = useState('');
  const [params, setParams] = useState<ParamSource[]>([]);

  const recipe = recipes.find((r) => r.id === source);
  const phone = phones.find((p) => p.id === phoneId) ?? phones[0];
  const list = phone ? templatesForPhone(phones, templates, phone.id) : [];
  const chosen = list.find((t) => `${t.name}|${t.language}` === tpl) ?? list[0];
  const count = chosen?.bodyParams ?? 0;
  const withOrder = recipe ? ORDER_TRIGGERS.includes(recipe.trigger) : false;
  const ready = name.trim() !== '' && (recipe ? Boolean(phone && chosen) : true);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const id = await submit.run(async () => {
      const res = await api.post<{ id: string }>('/flows', {
        name: name.trim(),
        ...(recipe && phone && chosen
          ? {
              recipe: {
                id: recipe.id,
                phoneNumberId: phone.id,
                templateName: chosen.name,
                templateLanguage: chosen.language,
                params: normalizeParams(count, params),
              },
            }
          : { trigger }),
      });
      return res.id;
    });
    if (id) navigate(`/automations/${id}`);
  };

  return (
    <Modal open={open} title="New automation" onClose={onClose}>
      <form className="stack" onSubmit={(e) => void onSubmit(e)}>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextField
          label="Name"
          value={name}
          maxLength={120}
          required
          onChange={(e) => setName(e.target.value)}
        />
        <SelectField
          label="Start from"
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setParams([]);
          }}
          options={[
            ...recipes.map((r) => ({ value: r.id, label: r.title })),
            { value: BLANK, label: 'Blank flow' },
          ]}
          hint={recipe?.summary ?? 'An empty canvas: choose a trigger and add your own steps.'}
        />
        {recipe ? (
          phones.length === 0 || !chosen ? (
            <Alert tone="info">
              {phones.length === 0 ? (
                <>
                  Connect a WhatsApp number first on the <Link to="/whatsapp">WhatsApp page</Link>.
                </>
              ) : (
                <>
                  This number has no approved template yet. Sync templates on the{' '}
                  <Link to="/whatsapp/templates">WhatsApp page</Link> and wait for Meta to approve
                  one.
                </>
              )}
            </Alert>
          ) : (
            <>
              <SelectField
                label="Send from"
                value={phone?.id ?? ''}
                onChange={(e) => {
                  setPhoneId(e.target.value);
                  setTpl('');
                  setParams([]);
                }}
                options={phones.map((p) => ({ value: p.id, label: p.label }))}
              />
              <SelectField
                label="Template"
                value={chosen ? `${chosen.name}|${chosen.language}` : ''}
                onChange={(e) => {
                  setTpl(e.target.value);
                  setParams([]);
                }}
                options={list.map((t) => ({
                  value: `${t.name}|${t.language}`,
                  label: `${t.name} (${t.language})`,
                }))}
              />
              <ParamsEditor
                count={count}
                value={params}
                onChange={setParams}
                withOrder={withOrder}
              />
            </>
          )
        ) : (
          <SelectField
            label="Start when"
            value={trigger}
            onChange={(e) => setTrigger(e.target.value as FlowTrigger)}
            options={FLOW_TRIGGERS.map((t) => ({ value: t, label: TRIGGER_LABEL[t].title }))}
            hint={TRIGGER_LABEL[trigger].when}
          />
        )}
        <div className="row">
          <Button type="submit" variant="primary" loading={submit.pending} disabled={!ready}>
            Create and open builder
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** `/automations`: your flows, today's usage against the limits, and a way to start a new one. */
export function AutomationsPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const tenantId = session.activeTenant?.id;
  const canManage = can('automations:manage');
  const [creating, setCreating] = useState(false);

  const flows = useLoader(
    () => api.get<{ flows: FlowRow[] }>('/flows').then((r) => r.flows),
    tenantId,
  );
  const usage = useLoader(() => api.get<Usage>('/flows/usage'), tenantId);
  const earned = useLoader(() => api.get<Attribution>('/analytics/attribution?days=30'), tenantId);
  const earnedBy = new Map(
    (earned.data?.sources ?? []).filter((x) => x.type === 'flow').map((x) => [x.id, x] as const),
  );
  const recipes = useLoader(
    () => api.get<{ recipes: RecipeRow[] }>('/flows/recipes').then((r) => r.recipes),
    tenantId,
  );

  const toggle = async (f: FlowRow) => {
    try {
      await api.post(`/flows/${f.id}/${f.status === 'paused' ? 'resume' : 'pause'}`, {});
      await flows.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const archive = async (f: FlowRow) => {
    const ok = await confirm({
      title: 'Archive automation',
      message: `Archive “${f.name}”? Runs that are waiting are cancelled and no new runs start.`,
      confirmLabel: 'Archive',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/flows/${f.id}`);
      await flows.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (flows.loading && !flows.data) return <Spinner size="lg" label="Loading" />;
  if (flows.error || !flows.data) {
    return <Alert tone="critical">{flows.error ?? 'Failed to load'}</Alert>;
  }
  const live = flows.data.filter((f) => f.status === 'active').length;
  const u = usage.data;

  return (
    <div className="stack-lg">
      <PageHeader
        title="Automations"
        description="Flows that react to orders, abandoned checkouts and WhatsApp messages. Every message respects the customer’s opt-in."
        actions={
          canManage ? (
            <Button variant="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
              New automation
            </Button>
          ) : null
        }
      />

      <div className="kpi-grid">
        <KpiCard label="Live automations" value={String(live)} />
        <KpiCard
          label="Runs started today"
          value={u ? `${u.runs.used} / ${u.runs.limit}` : null}
          hint={
            u && u.runs.blocked > 0 ? `${u.runs.blocked} dropped at the daily limit` : 'UTC day'
          }
        />
        <KpiCard
          label="Earned in 30 days"
          value={
            earned.data ? formatMoney(earned.data.attributedRevenue, earned.data.currency) : null
          }
          hint={
            earned.data
              ? `${earned.data.attributedOrders} orders within ${earned.data.windowDays} days of a delivered message`
              : undefined
          }
        />
        <KpiCard
          label="Messages queued today"
          value={u ? `${u.messages.used} / ${u.messages.limit}` : null}
          hint={
            u && u.messages.blocked > 0
              ? `${u.messages.blocked} skipped at the daily limit`
              : 'UTC day'
          }
        />
      </div>

      {flows.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Zap size={22} />}
            title="No automations yet"
            action={
              canManage ? (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  New automation
                </Button>
              ) : undefined
            }
          >
            Start from a ready-made recovery flow or build your own. Nothing is sent until you
            publish.
          </EmptyState>
        </Card>
      ) : (
        <div className="flow-grid">
          {flows.data.map((f) => (
            <div key={f.id} className="rule-card stack">
              <div className="rule-card__head">
                <div>
                  <div className="rule-card__title">{f.name}</div>
                  <div className="text-muted text-sm">
                    {f.trigger ? TRIGGER_LABEL[f.trigger]?.title : 'No trigger'}
                  </div>
                </div>
                <Badge tone={STATUS_TONE[f.status]}>{STATUS_LABEL[f.status]}</Badge>
              </div>
              {earnedBy.get(f.id) && earned.data && (
                <div className="text-sm">
                  <strong>{formatMoney(earnedBy.get(f.id)!.revenue, earned.data.currency)}</strong>{' '}
                  from {earnedBy.get(f.id)!.orders} order
                  {earnedBy.get(f.id)!.orders === 1 ? '' : 's'} in 30 days
                </div>
              )}
              <div className="text-muted text-sm">
                {f.activeVersion !== null ? `Version ${f.activeVersion} live` : 'Never published'}
                {f.hasUnpublishedDraft ? ' · unpublished changes' : ''}
                {' · '}edited {formatRelative(f.updatedAt)}
              </div>
              <div className="row">
                <Link to={`/automations/${f.id}`} className="btn btn--secondary btn--sm">
                  {canManage ? 'Open builder' : 'View'}
                </Link>
                {canManage && f.activeVersion !== null && (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={f.status === 'paused' ? <Play size={14} /> : <Pause size={14} />}
                    onClick={() => void toggle(f)}
                  >
                    {f.status === 'paused' ? 'Resume' : 'Pause'}
                  </Button>
                )}
                {canManage && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 size={14} />}
                    aria-label={`Archive ${f.name}`}
                    onClick={() => void archive(f)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <NewAutomation
          open
          recipes={recipes.data ?? []}
          tenantId={tenantId}
          onClose={() => {
            setCreating(false);
            void flows.reload();
          }}
        />
      )}
    </div>
  );
}
